<!-- 
Dialog for creating or editing a risk factor range.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="levy-collection-setup" section="risk-factors"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex v-if="show" xs12 sm6>
                <v-text-field 
                  v-if="isInteger"
                  :value="factor.lowerBound" :rules="[rules.integer]"
                  :label="label"
                  :hint="hint"
                  autofocus
                  @input="update('lowerBound', $event)"
                />
                <our-percentage-input
                  v-if="isPercentage"
                  :value="factor.lowerBound" :decimal-places="1"
                  :label="label"
                  :hint="hint"
                  autofocus
                  unrestricted-range
                  @input="update('lowerBound', $event)"
                />
                <v-text-field 
                  v-if="isNumber"
                  :value="factor.lowerBound" :rules="[rules.number]"
                  :label="label"
                  :hint="hint"
                  autofocus
                  @input="update('lowerBound', $event)"
                />
                <v-select 
                  v-if="isBoolean"
                  :items="lookups.trueFalse"
                  :value="factor.lowerBound"
                  :label="label"
                  :hint="hint"
                  autofocus
                  clearable
                  @input="update('lowerBound', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="factor.score" :rules="[rules.number, rules.required]"
                  :label="terminology + '*'" required @input="update('score', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  :value="defaultLabel"
                  :disabled="useCustomLabel"
                  :label="(forSandbox ? 'Label' : 'Default label')" 
                  hint="The default label is generated automatically. Use a custom label to override it."
                  readonly
                />
              </v-flex>
              <v-flex v-if="!forSandbox" xs12 sm6>
                <v-layout>
                  <v-flex shrink>
                    <v-checkbox
                      v-model="useCustomLabel" 
                      color="primary"
                    />
                  </v-flex>
                  <v-flex grow>
                    <v-text-field 
                      :disabled="!useCustomLabel"
                      :value="factor.customLabel" 
                      :rules="[(useCustomLabel ? rules.required : rules.empty)]"
                      :label="(useCustomLabel ? 'Custom label*' : 'Custom label')" 
                      @input="update('customLabel', $event)" 
                    />
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat color="success" 
            type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog
      v-model="showValidationDialog"
      :form="this.$refs.form"
      :state-error="state.error"
      :specified-error="specifiedError"
    />
  </v-dialog>
</template>

<script>
import _ from "underscore";
import * as action from "@/store/levy-collection-risk-factors/action-types";
import * as mutation from "@/store/levy-collection-risk-factors/mutation-types";
import * as formatting from "@/helpers/formatting.helper";
import filterMixins from "@/mixins/filter.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import lookupMixins from "@/mixins/lookup.mixins";

export default {
  mixins: [ruleMixins, lookupMixins, filterMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    },
    riskFactorType: {
      type: String,
      required: true
    },
    riskFactorId: {
      validator: prop => typeof prop === "number" || prop == null,
      default: null
    },
    lowerBoundLabel: {
      type: String,
      required: true
    },
    terminology: {
      type: String,
      required: true
    },
    forSandbox: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false,
      useCustomLabel: false,
      specifiedError: null
    };
  },
  computed: {
    create() {
      return !this.riskFactorId;
    },
    state() {
      const s = this.$store.state.levyCollectionRiskFactors;
      return this.create ? s.create : s.edit;
    },
    factor() {
      return this.state.data;
    },
    loading() {
      return this.state.loading;
    },
    busy() {
      return this.create ? this.state.saving : this.state.loading || this.state.saving;
    },
    title() {
      return this.create ? "Add new risk factor" : "Edit risk factor";
    },
    isPercentage() {
      return this.doesLookupHaveAttribute(this.riskFactorType, "IsPercentage", this.lookups.riskFactorTypes);
    },
    isInteger() {
      return this.doesLookupHaveAttribute(this.riskFactorType, "IsInteger", this.lookups.riskFactorTypes);
    },
    isBoolean() {
      return this.doesLookupHaveAttribute(this.riskFactorType, "IsBoolean", this.lookups.riskFactorTypes);
    },
    isNumber() {
      return !this.isPercentage && !this.isInteger && !this.isBoolean;
    },
    isLowerBoundExclusive() {
      return this.doesLookupHaveAttribute(this.riskFactorType, "LowerBoundIsExclusive", this.lookups.riskFactorTypes);
    },
    label() {
      if (this.isBoolean) {
        return `${this.lowerBoundLabel} is`;
      }
      return this.isLowerBoundExclusive
        ? `${this.lowerBoundLabel} greater than`
        : `${this.lowerBoundLabel} greater than or equal to`;
    },
    existingFactors() {
      return this.$store.state.levyCollectionRiskFactors.listing.data.filter(r => r.type == this.riskFactorType);
    },
    defaultLabel() {
      let description = formatting.getRiskFactorLowerBoundDescription(
        this.factor,
        this.existingFactors,
        this.getLookup(this.riskFactorType, this.lookups.riskFactorTypes)
      );
      return description;
    },
    hint() {
      return "Leave blank if there is no lower bound.";
    },
    terminologyLowerCase() {
      return this.terminology.toLowerCase();
    },
    isDuplicate() {
      return _(this.existingFactors).some(
        f => formatting.isSame(f.lowerBound, this.factor.lowerBound) && f.id != this.factor.id
      );
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.resetValidation();
        if (!this.create) {
          this.$store.dispatch(action.EDIT_GET, {
            levyCollectionId: this.levyCollectionId,
            riskFactorId: this.riskFactorId
          });
        } else {
          this.$store.commit(mutation.CREATE_INITIALISE, this.riskFactorType);
        }
      }
    },
    loading(isLoading) {
      if (!isLoading && !this.create) {
        this.useCustomLabel = !!this.factor.customLabel;
      }
    },
    useCustomLabel(custom) {
      if (!custom) this.$refs.form.resetValidation();
    }
  },
  methods: {
    update(field, value) {
      const m = this.create ? mutation.CREATE_SET_DATA : mutation.EDIT_SET_DATA;
      this.$store.commit(m, { field, value });
    },
    async save() {
      this.update("type", this.riskFactorType);
      if (!this.useCustomLabel) this.update("customLabel", null);
      this.specifiedError = null;
      let successful;
      if (this.isDuplicate) {
        successful = false;
        this.specifiedError = `There is already a range for "${this.defaultLabel}".`;
      } else if (this.create) {
        successful = this.$refs.form.validate() && (await this.$store.dispatch(action.CREATE, this.levyCollectionId));
      } else {
        successful = this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_SAVE));
      }
      if (successful) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
